/**
 * dexRegionStabilizer.ts — b17d Fix 3.
 *
 * Region-change latch for the live Dex scanner.
 *
 * Root cause (TestFlight session, 53 frames): the per-frame region classifier
 * latched onto `hisui` — a 7-species mini-region (National Dex 899-905) — for 8
 * consecutive frames before settling on `kanto`. A single low-confidence frame
 * misreading the region is enough to route a write to the WRONG region's dex,
 * so the classifier must not emit a region change off one noisy frame.
 *
 * Fix: buffer the last N (default 3) per-frame region classifications and only
 * emit a region change once N consecutive frames AGREE and each cleared a
 * confidence threshold. Until the buffer stabilises we return the previous
 * stable region (or `null` when there is no stable region yet — the caller
 * drops the frame). This is the same hysteresis idea as `PipScreenSmoother`,
 * specialised for the Dex region so it can be unit-tested in isolation against
 * the TestFlight hisui-latch evidence.
 *
 * Pure, side-effect-free apart from one diagnostic `console.log` when a new
 * region becomes stable (`[dex-scan] region stabilised`) so the device trace
 * proves the latch is working. No DOM. The region map (1-151 kanto … 899-905
 * hisui … 906-1025 paldea) is owned elsewhere and intentionally untouched here.
 */

import type { PokedexRegionLite } from "./pogoPokedexVariantClassifier";

/**
 * Consecutive agreeing frames required before a region change is emitted.
 * The live scan runs ~1-3 Hz, so 3 frames is ~1-3 s of agreement — slow enough
 * to ignore the single-frame hisui misreads in the TestFlight log, fast enough
 * to follow a real region switch when the user scrolls into a new dex tab.
 */
export const REGION_STABLE_FRAMES = 3;

/**
 * Minimum per-frame confidence (0-100) for a frame's region to count toward
 * stabilisation. The hisui-latch frames in the log arrived at confidence 0 and
 * 100 both, so confidence alone is not sufficient — but a frame BELOW this bar
 * never contributes to (or resets) the agreement run. Frames at/above it do.
 */
export const REGION_MIN_CONFIDENCE = 75;

export interface RegionFrameInput {
  /** Region the per-frame classifier read for this frame. */
  region: PokedexRegionLite;
  /** OCR/classifier confidence for this frame (0-100). */
  confidence: number;
}

export interface RegionStabilizeResult {
  /**
   * The committed STABLE region (promotion hysteresis): the region only N
   * consecutive agreeing frames may change, or `null` when none has stabilised
   * yet. This is the long-lived region identity; it is deliberately slow to move
   * so a single noisy frame can never flip it (the hisui-latch guard).
   *
   * NOTE: this is NOT the region a frame should be slot-mapped under — use
   * `routingRegion` for that. `region` stays here for the stable-identity /
   * promotion semantics the rest of the session relies on.
   */
  region: PokedexRegionLite | null;
  /**
   * The region the caller should SLOT-MAP / ROUTE this frame under.
   *
   * Strong-same-frame-wins (telemetry 2026-06-22, G-Max #003/#006/#009 bug):
   * when THIS frame's own OCR read a region at/above the confidence floor that
   * CONTRADICTS the stale stable region, the frame's own region wins for
   * routing — the stale stable region must never slot-map a confident current
   * read (which mapped Kanto #003/#006/#009 onto Galar #810/#811/#812). The
   * promotion hysteresis above is untouched, and every write still clears the
   * downstream two-frame corroboration + membership/slot-range gates, so a lone
   * noisy contradicting frame routes under its own region but cannot WRITE off a
   * single frame.
   *
   * When the frame is weak (below the floor, or `unknown`) it carries no region
   * of its own, so `routingRegion` falls back to the stable region — sticky
   * context only ever fills a missing/weak current region, never overwrites a
   * confident one. `null` when neither a confident current region nor a stable
   * region exists (caller drops the frame).
   */
  routingRegion: PokedexRegionLite | null;
  /** True only on the frame where a NEW region just became stable. */
  changed: boolean;
  /** Diagnostic — consecutive agreeing frames behind the current candidate. */
  framesAgreed: number;
}

export interface DexRegionStabilizerOptions {
  /** Override the consecutive-frame gate (mainly for tests). */
  stableFrames?: number;
  /** Override the per-frame confidence floor (mainly for tests). */
  minConfidence?: number;
}

/**
 * Stateful region latch. Construct once per live-scan session, feed each
 * frame's `(region, confidence)` via `next()`, and trust the returned region.
 *
 * Contract:
 *   - A frame whose confidence is below `minConfidence`, or whose region is
 *     `"unknown"`, never advances the agreement run — it returns the current
 *     stable region (which may be `null`) and leaves the candidate untouched.
 *   - A qualifying frame that matches the running candidate increments the run;
 *     once it reaches `stableFrames` the candidate is promoted to the stable
 *     region. Only THAT promotion frame reports `changed: true`.
 *   - A qualifying frame that differs from the candidate restarts the run at 1
 *     for the new candidate.
 */
export class DexRegionStabilizer {
  private stableRegion: PokedexRegionLite | null = null;
  private candidate: PokedexRegionLite | null = null;
  private candidateCount = 0;
  private readonly stableFrames: number;
  private readonly minConfidence: number;

  constructor(opts: DexRegionStabilizerOptions = {}) {
    this.stableFrames = opts.stableFrames ?? REGION_STABLE_FRAMES;
    this.minConfidence = opts.minConfidence ?? REGION_MIN_CONFIDENCE;
  }

  reset(): void {
    this.stableRegion = null;
    this.candidate = null;
    this.candidateCount = 0;
  }

  /** Current stable region without feeding a frame (null until first stabilise). */
  current(): PokedexRegionLite | null {
    return this.stableRegion;
  }

  next(input: RegionFrameInput): RegionStabilizeResult {
    const { region, confidence } = input;

    // Does THIS frame carry a confident region of its own? Only such a frame may
    // win over a contradicting stale stable region for routing.
    const frameRegionStrong = region !== "unknown" && confidence >= this.minConfidence;

    // Low-confidence or unreadable frames carry no region signal: they neither
    // advance nor reset the agreement run. The caller keeps trusting whatever
    // already stabilised (possibly null → drop the frame). A weak frame has no
    // region of its own, so it routes under the stable region (sticky fill).
    if (!frameRegionStrong) {
      return {
        region: this.stableRegion,
        routingRegion: this.stableRegion,
        changed: false,
        framesAgreed: this.candidateCount,
      };
    }

    // Already the stable region — keep the run saturated and report no change.
    if (region === this.stableRegion) {
      this.candidate = region;
      this.candidateCount = this.stableFrames;
      return {
        region: this.stableRegion,
        routingRegion: this.stableRegion,
        changed: false,
        framesAgreed: this.candidateCount,
      };
    }

    if (region === this.candidate) {
      this.candidateCount += 1;
    } else {
      this.candidate = region;
      this.candidateCount = 1;
    }

    if (this.candidateCount >= this.stableFrames) {
      this.stableRegion = region;
      this.candidateCount = this.stableFrames; // clamp
      // eslint-disable-next-line no-console
      console.log("[dex-scan] region stabilised", {
        region,
        framesAgreed: this.stableFrames,
      });
      return {
        region: this.stableRegion,
        routingRegion: this.stableRegion,
        changed: true,
        framesAgreed: this.candidateCount,
      };
    }

    // Candidate not yet stable. The committed stable region does NOT move (the
    // promotion hysteresis that guards against a single-frame misread flipping
    // the long-lived identity). But this frame read a CONFIDENT region that
    // contradicts the stale stable region, so it must route under its OWN region
    // — never let the stale region slot-map a confident current read (the G-Max
    // #003/#006/#009 → Galar #810/#811/#812 bug). Downstream two-frame
    // corroboration + membership/slot-range gates still gate any actual write,
    // so a lone noisy contradicting frame routes under its own region but cannot
    // write off one frame.
    return {
      region: this.stableRegion,
      routingRegion: region,
      changed: false,
      framesAgreed: this.candidateCount,
    };
  }
}
